import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { websites } from '@/mocks/websites';
import { articles } from '@/mocks/articles';
import SEOMeta from '@/components/feature/SEOMeta';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';
import WebsiteCard from '@/pages/home/components/WebsiteCard';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [tab, setTab] = useState<'websites' | 'articles'>('websites');

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  const filteredWebsites = useMemo(() => {
    if (!slug || slug === 'all') return websites;
    return websites.filter((w) => w.category === slug);
  }, [slug]);

  const filteredArticles = useMemo(() => {
    if (!slug || slug === 'all') return articles;
    return articles.filter((a) => a.category === slug);
  }, [slug]);

  const breadcrumbSchema = {
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanman.site/' },
      { '@type': 'ListItem', position: 2, name: currentCategory.name, item: `https://hanman.site/category/${currentCategory.slug}` },
    ],
  };

  return (
    <main>
      <SEOMeta
        title={`${currentCategory.name} - 韩漫平台导航 | 韩漫导航`}
        description={`${currentCategory.description}，汇集${currentCategory.name}类韩漫平台入口，提供免费韩漫网址导航与在线观看汇总。`}
        keywords={`${currentCategory.name},韩漫网站,韩漫平台,免费韩漫,韩漫导航`}
        canonicalUrl={`https://hanman.site/category/${currentCategory.slug}`}
      />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />

      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: currentCategory.name },
        ]}
      />

      <section className="w-full px-4 md:px-6 pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                {currentCategory.name}
              </h1>
              <p className="text-foreground-600 text-sm">
                {currentCategory.description}，共收录
                <strong className="text-primary-600 mx-1">{filteredWebsites.length}</strong>
                个韩漫平台
              </p>
            </div>
          </div>

          {/* Category tabs */}
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  currentCategory.slug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Tab switch */}
          <div className="flex gap-1 p-1 bg-background-100 rounded-full mb-8 w-fit">
            <button
              onClick={() => setTab('websites')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                tab === 'websites' ? 'bg-white text-foreground-950' : 'text-foreground-600 hover:text-foreground-800'
              }`}
            >
              <i className="ri-global-line mr-1.5"></i>
              韩漫平台 ({filteredWebsites.length})
            </button>
            <button
              onClick={() => setTab('articles')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                tab === 'articles' ? 'bg-white text-foreground-950' : 'text-foreground-600 hover:text-foreground-800'
              }`}
            >
              <i className="ri-article-line mr-1.5"></i>
              相关文章 ({filteredArticles.length})
            </button>
          </div>

          {tab === 'websites' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredWebsites.map((website) => (
                <WebsiteCard
                  key={website.id}
                  name={website.name}
                  url={website.url}
                  description={website.description}
                  tags={website.tags}
                  rating={website.rating}
                  image={website.image}
                />
              ))}
              {filteredWebsites.length === 0 && (
                <div className="col-span-full text-center py-20 text-foreground-400">
                  <i className="ri-inbox-line text-5xl mb-4 block"></i>
                  <p>该分类暂无韩漫平台</p>
                </div>
              )}
            </div>
          )}

          {tab === 'articles' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                  className="group bg-white rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                >
                  <div className="relative w-full h-44 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <h2 className="font-heading text-base font-bold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3 line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {article.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {article.readTime}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
              {filteredArticles.length === 0 && (
                <div className="col-span-full text-center py-20 text-foreground-400">
                  <i className="ri-file-text-line text-5xl mb-4 block"></i>
                  <p>该分类暂无相关文章</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}